import React from "react";
import { Flex, Select, InputNumber, Input, Button, Tooltip } from "antd";
import { FaRegCheckCircle } from "react-icons/fa";
import { useCurrentUser } from "../../../../contexts/UserContext";
import { useViewerStore } from "../../../../store/store";
import { Toggle } from "../../../../components";
import NotesTextArea from "../../../../components/Pure-UI-Components/Inputs/NotesTextArea";
import { PencilSimpleIcon, CopyIcon } from "@phosphor-icons/react";
import {
    QUESTIONABLE_ROOF_OPTION_OTHER_NOTES_ID
} from "../../../../components/Layout-Components/Editor-Layout/Modal/Modal-Content/QuestionableRoof";
import { BuildingDesignData, RoofDesignData } from "../../../../@types/types";
import { AlertType } from "../../../../@types/enums";
import RoofPropertiesPopup from "./RoofPropertiesPopup";

interface PrimaryRoofAttributesProps {
    selectedBuilding: BuildingDesignData,
    setSelectedBuilding: React.Dispatch<React.SetStateAction<BuildingDesignData | undefined>>;
    handleCopyOldRoofAttributes?: (index: number, roofData: RoofDesignData | null) => void;
    showInactive: any,
    readOnly: any,
    isFromPopup?: boolean,
}

const {Option} = Select;

const PrimaryRoofAttributes: React.FC<PrimaryRoofAttributesProps> = ({
    selectedBuilding,
    setSelectedBuilding,
    handleCopyOldRoofAttributes,
    showInactive,
    readOnly,
    isFromPopup = false,
}) => {
    const {user, hasCapability} = useCurrentUser();
    const {sceneViewer, masterDataList, ballastedRackModuleAngle, designList} = useViewerStore.getState();

    const qfoptions = [
        {value: true, label: "Yes"},
        {value: false, label: "No"},
    ];

    const defaultOption = { value: 0, disabled: true, label: "Select Reason" };
    const qfNotesOptions = [defaultOption, ...(masterDataList?.QuestionableRoof || [])];

    function metersToFeetAndInches(meters: number) {
        const feet = meters * 3.28084;
        const wholeFeet = Math.floor(feet);
        const remainingInches = (feet - wholeFeet) * 12;
        let roundedInches = Math.round(remainingInches);
        let adjustedFeet = wholeFeet;
        if (roundedInches === 12) {
            adjustedFeet += 1;
            roundedInches = 0;
        }

        return {
            feet: adjustedFeet,
            inches: roundedInches,
        };
    }

    function feetAndInchesToMeters(feet: number | string, inches: number | string) {
        const metersFromFeet = (Number(feet) ?? 0) / 3.28084;
        const metersFromInches = (Number(inches) ?? 0) * 0.0254;
        const meters = metersFromFeet + metersFromInches;
        return meters;
    }

    const handleChange = <K extends keyof RoofDesignData>(
        field: K,
        value: RoofDesignData[K],
        index: number,
        unit?: string
    ) => {
        const newBuildingData: BuildingDesignData = { ...selectedBuilding };
        let newValue = value;

        if (field === 'Rafter_Size') {
            const currentSize = newBuildingData.Roofs[index].Rafter_Size?.includes('X')
                ? newBuildingData.Roofs[index].Rafter_Size
                : "0X0";
            let [feet, inches] = currentSize.split("X").map(Number);
            newValue = (unit === "ft"
                ? `${value}X${inches}`
                : `${feet}X${value}`) as RoofDesignData[K];
        } else if (field === 'Questionable_Roof' && !value) {
            newBuildingData.Roofs[index].Questionable_Roof_Id = 0;
        } else if (field === 'Questionable_Roof_Notes') {
            const roofMesh = sceneViewer.roofMeshArr[index];
            const roofNoteId = newBuildingData.Roofs[index].Questionable_Roof_Id ?? 0;
            const otherReason = newBuildingData.Roofs[index].Questionable_Roof_Notes;

            sceneViewer.setQuestionable(roofMesh, roofNoteId, otherReason, (status: boolean, id: number, notes: string) => {
                newBuildingData.Roofs[index].Questionable_Roof = status;
                newBuildingData.Roofs[index].Questionable_Roof_Id = id;
                newBuildingData.Roofs[index].Questionable_Roof_Notes = notes;
                setSelectedBuilding(newBuildingData);
                useViewerStore.setState({
                    isRoofChange: true,
                    isEngineeringFieldsChange: true,
                    isRefreshDone: false,
                });
            });
            return;
        } else if (field === 'Unsupported_Rafter_Span' || field === 'Max_Span') {
            let { feet, inches } = metersToFeetAndInches(newBuildingData.Roofs[index][field] as number);
            let ms_value = unit === "ft"
                ? feetAndInchesToMeters(Number(value), inches)
                : feetAndInchesToMeters(feet, Number(value));
            newValue = Number(Math.round((ms_value + Number.EPSILON) * 100) / 100) as RoofDesignData[K];
        }

        newBuildingData.Roofs[index][field] = newValue;

        setSelectedBuilding(newBuildingData);
        useViewerStore.setState({
            isRoofChange: true,
            isEngineeringFieldsChange: true,
            isRefreshDone: false,
        });
    };

    const highlightRoof = (roofName: string, value: boolean) => {
        if (roofName.includes('Roof ')) {
            const roofId = roofName.split(' ')[1];
            sceneViewer.highlightRoof(roofId, value);
        }
    }

    return (
        <>
            {selectedBuilding &&
                user &&
                selectedBuilding.Roofs.map((item, index) => {
                    if (!item) return null;
                    return (
                    <div
                        key={index}
                        onMouseEnter={() => highlightRoof(item.Name, true)}
                        onMouseLeave={() => highlightRoof(item.Name, false)}
                    >
                        <>
                            {(showInactive || isFromPopup || item.Number_of_Modules > 0) && (
                                <div className="tab__column obst-edit-mask" key={index}>
                                    <span className="tab__column--subtitle">
                                        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
                                        <Flex align="center">
                                            {item.Name_Override ? `Roof ${item.Name_Override}` : item.Name}
                                            {item.Number_of_Modules > 0 && (
                                                <FaRegCheckCircle
                                                    color="green"
                                                    style={{ marginLeft: 10 }}
                                                />
                                            )}
                                        </Flex>
                                          {!isFromPopup && designList.length > 2 && (
                                                <Tooltip title="Copy Old Roof Attributes">
                                                    <CopyIcon
                                                        size={18}
                                                        style={{ cursor: "pointer" }}
                                                        onClick={async () => {
                                                            sceneViewer.viewerAlert({
                                                                show: true,
                                                                title: "Copy Old Roof Attributes",
                                                                message: <RoofPropertiesPopup
                                                                    selectedBuilding={selectedBuilding}
                                                                    handleCopy={handleCopyOldRoofAttributes}
                                                                    initialRoofIndex={index}
                                                                />,
                                                                messageType: AlertType.info,
                                                                isModal: true
                                                            })
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Flex>
                                    </span>

                                    {hasCapability("Toggle_Engg_Letter") && (
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className="small_input_flex"
                                        >
                                            <div className="input_label">
                                                <span className="tab__input--label small_input_label ">
                                                    Include in Letter:
                                                </span>
                                            </div>
                                            <Toggle
                                                on={item.Include_In_Letter}
                                                onChange={(value: boolean) => handleChange('Include_In_Letter', value, index)}
                                                size="small"
                                                disabled={item.Number_of_Modules < 1}
                                            />
                                        </Flex>
                                    )}
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label ">
                                                Rafter Size(In):
                                            </span>
                                        </div>
                                        <Flex align="center" justify="space-between" style={{ width: "60%" }} gap={5}>
                                            <InputNumber
                                                value={item.Rafter_Size?.includes('X') ? (parseFloat(item.Rafter_Size.split("X")[0]) || 0) : 0}
                                                min={0}
                                                onChange={(value) => {if(value !== null) handleChange('Rafter_Size', String(value), index, "ft")}}
                                                className="small-input-feild"
                                                disabled={readOnly}
                                            />
                                            <span className="tab__input--label small_input_label">X</span>
                                            <InputNumber
                                                value={item.Rafter_Size?.includes('X') ? (parseFloat(item.Rafter_Size.split("X")[1]) || 0) : 0}
                                                min={0}
                                                onChange={(value) => {if(value !== null) handleChange('Rafter_Size', String(value), index, "in")}}
                                                className="small-input-feild"
                                                disabled={readOnly}
                                            />
                                        </Flex>
                                    </Flex>

                                    <Flex align="center" justify="space-between" className="small_input_flex">
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">Qty of Existing Rafters:</span>
                                        </div>
                                        <InputNumber
                                            className="small-input-feild"
                                            value={item.Existing_Rafter_Qty ?? ""}
                                            min={0}
                                            onChange={(value) => {
                                                if (typeof value === 'number') {
                                                    handleChange('Existing_Rafter_Qty', value, index);
                                                }
                                            }}
                                        />
                                    </Flex>

                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Unsupported Rafter Span:
                                            </span>
                                        </div>
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            style={{ width: "60%" }}
                                            gap={5}
                                        >
                                            <InputNumber
                                                min={0}
                                                max={99}
                                                precision={0}
                                                value={metersToFeetAndInches(item.Unsupported_Rafter_Span).feet}
                                                onChange={(value) => {if(value !== null) handleChange('Unsupported_Rafter_Span', value, index, "ft")}}
                                                className="small-input-feild"
                                                disabled={readOnly} />
                                            <span className="tab__input--label small_input_label">
                                                Ft
                                            </span>
                                            <InputNumber
                                                min={0}
                                                max={11}
                                                precision={0}
                                                value={metersToFeetAndInches(item.Unsupported_Rafter_Span).inches}
                                                onChange={(value) => {if(value !== null) handleChange('Unsupported_Rafter_Span', value, index, "in")}}
                                                className="small-input-feild"
                                                disabled={readOnly} />
                                            <span className="tab__input--label small_input_label">
                                                In
                                            </span>
                                        </Flex>
                                    </Flex>

                                    <Flex align="center" justify="space-between" className="small_input_flex">
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Rafter Spacing (In):
                                            </span>
                                        </div>
                                        <InputNumber 
                                            className="small-input-feild"
                                            min={0}
                                            max={99}
                                            precision={0}
                                            value={
                                                item.Rafter_Spacing?.toString().includes('X')
                                                    ? parseInt(item.Rafter_Spacing.toString().split('X')[0])
                                                    : typeof item.Rafter_Spacing === 'number'
                                                        ? item.Rafter_Spacing
                                                        : null
                                            }
                                            onChange={(value) => handleChange('Rafter_Spacing', value, index)}
                                            disabled={readOnly}
                                        />
                                    </Flex>

                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Max Span (Ft/In):
                                            </span>
                                        </div>
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            style={{ width: "60%" }}
                                            gap={5}
                                        >
                                            <InputNumber
                                                min={0}
                                                max={99}
                                                precision={0}
                                                value={metersToFeetAndInches(item.Max_Span).feet}
                                                onChange={(value) => {
                                                    if (value !== null) {
                                                        handleChange('Max_Span', value, index, "ft");
                                                    }
                                                }}
                                                className="small-input-feild"
                                                disabled={readOnly} />

                                            <span className="tab__input--label small_input_label">
                                                Ft
                                            </span>
                                            <InputNumber
                                                min={0}
                                                max={11}
                                                precision={0}
                                                value={metersToFeetAndInches(item.Max_Span).inches}
                                                onChange={(value) => {
                                                    if (value !== null) {
                                                        handleChange('Max_Span', value, index, "in");
                                                    }
                                                }}
                                                className="small-input-feild"
                                                disabled={readOnly} />
                                            <span className="tab__input--label small_input_label">
                                                In
                                            </span>
                                        </Flex>
                                    </Flex>

                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Roof Type:
                                            </span>
                                        </div>
                                        <Select
                                            value={
                                                item.Roof_Type_Id
                                                    ? item.Roof_Type_Id === 1
                                                        ? ""
                                                        : item.Roof_Type_Id
                                                    : ""
                                            }
                                            onChange={(value) => handleChange('Roof_Type_Id', Number(value), index)}
                                            className="small-input-feild"
                                            disabled={readOnly}
                                        >
                                            {masterDataList?.RoofType?.map((option, idx) => (
                                                <Option key={idx} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Flex>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Structure Type:{" "}
                                            </span>
                                        </div>
                                        <Select
                                            value={item.Structure_Type_Id}
                                            onChange={(value) => handleChange('Structure_Type_Id', value, index)}
                                            className="small-input-feild"
                                            disabled={readOnly}
                                        >
                                            {masterDataList?.StructureType?.map((option, idx) => (
                                                <Option key={idx} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Flex>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Roof Condition:{" "}
                                            </span>
                                        </div>
                                        <Select
                                            value={item.Roof_Condition_Id}
                                            onChange={(value) => handleChange('Roof_Condition_Id', value, index)}
                                            className="small-input-feild"
                                            disabled={readOnly}
                                        >
                                            {masterDataList?.RoofCondition?.map((option, idx) => (
                                                <Option key={idx} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Flex>
                                    {hasCapability("Toggle_Engg_Letter") && (
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className="small_input_flex"
                                        >
                                            <div className="input_label">
                                                <span className="tab__input--label small_input_label">
                                                    PSF:
                                                </span>
                                            </div>
                                            <Select
                                                value={item.PSF_Id ? item.PSF_Id : ""}
                                                onChange={(value) => handleChange('PSF_Id', Number(value), index)}
                                                className="small-input-feild"
                                                disabled={readOnly}
                                            >
                                                {masterDataList?.PSF?.map((option, idx) => (
                                                    <Option key={idx} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Flex>
                                    )}
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Notes:
                                            </span>
                                        </div>
                                        <NotesTextArea
                                            value={item.Notes}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('Notes', e.target.value, index)}
                                            disabled={readOnly}
                                        />
                                    </Flex>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Questionable Roof:
                                            </span>
                                        </div>
                                        <Select
                                            value={item.Questionable_Roof}
                                            onChange={(value) => handleChange('Questionable_Roof', value, index)}
                                            className="small-input-feild"
                                            disabled={readOnly}
                                        >
                                            {qfoptions.map((option, idx) => (
                                                <Option key={idx} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>

                                    </Flex>
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Questionable Roof Notes:
                                            </span>
                                        </div>
                                        <Select
                                            value={item.Questionable_Roof_Id}
                                            onChange={(value) => handleChange('Questionable_Roof_Id', value, index)}
                                            className="small-input-feild"
                                            disabled={readOnly || (item.Questionable_Roof !== true)}
                                        >
                                            {qfNotesOptions.map((option, idx) => (
                                                <Option key={idx} value={option.value} disabled={option.disabled} >
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Flex>

                                    {
                                        item.Questionable_Roof_Id === QUESTIONABLE_ROOF_OPTION_OTHER_NOTES_ID &&

                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className="small_input_flex"
                                        >
                                            <div className="input_label">
                                                <span className="tab__input--label small_input_label">Other Reason Notes:</span>
                                            </div>
                                            <Input
                                                className="small-input-feild"
                                                value={item.Questionable_Roof_Notes}
                                                disabled={true}

                                            />
                                            <Tooltip title="Edit Notes">
                                                <Button
                                                    style={{
                                                        height: "30px",
                                                    }}
                                                    icon={<PencilSimpleIcon />}
                                                    onClick={(e) => handleChange('Questionable_Roof_Notes', item.Questionable_Roof_Notes, index)}
                                                >
                                                </Button>
                                            </Tooltip>
                                        </Flex>
                                    }

                                    {hasCapability("Toggle_Engg_Letter") && (
                                        <Flex
                                            align="center"
                                            justify="space-between"
                                            className="small_input_flex"
                                        >
                                            <div className="input_label">
                                                <span className="tab__input--label small_input_label">
                                                    Dead Load:
                                                </span>
                                            </div>
                                            <Select
                                                value={item.Dead_Load_Id ? item.Dead_Load_Id : ""}
                                                onChange={(value) => handleChange('Dead_Load_Id', Number(value), index)}
                                                className="small-input-feild"
                                                disabled={readOnly}
                                            >
                                                {masterDataList?.DeadLoad?.map((option, idx) => (
                                                    <Option key={idx} value={option.value}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Flex>
                                    )}
                                    <Flex
                                        align="center"
                                        justify="space-between"
                                        className="small_input_flex"
                                    >
                                        <div className="input_label">
                                            <span className="tab__input--label small_input_label">
                                                Roof Has <br /> Attic Access:
                                            </span>
                                        </div>
                                        <Select
                                            value={item.Roof_Has_Attic_Acces === null ? "" : item.Roof_Has_Attic_Acces}
                                            onChange={(value) => handleChange('Roof_Has_Attic_Acces', value, index)}
                                            className="small-input-feild"
                                            disabled={readOnly}
                                        >
                                            {qfoptions.map((option, index) => (
                                                <Option key={index} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Flex>
                                </div>
                            )}
                        </>
                    </div>
                )})}
        </>
    );
};

export default PrimaryRoofAttributes;
